import { CellValueChangedEvent } from 'ag-grid-community';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import 'styles/ag-grid.css';
import { IPerilSettingsTableProps } from './types';
import { subLimitSettingsApi } from 'services/apis';
import { EQueryKeys } from 'constants/query-keys';
import { useMutation, useQuery } from 'react-query';
import { UpdateSubLimitSettingsPayloadDto } from 'libs/pinnacle-sdk';
import { handleError } from 'utils/form';
import ExcelTable from 'components/ui/excel-table';
import {
  EExcelTableColumnType,
  EExcelTablePaginationType,
  ExcelTableColDef,
} from 'components/ui/excel-table/types';
import { isSubmissionEditable } from 'utils/submission';

const PerilSettingsTable: React.FC<IPerilSettingsTableProps> = ({
  submission,
}) => {
  const { t } = useTranslation();
  const isEditingMode = useMemo(() => {
    return isSubmissionEditable(submission.status);
  }, [submission.status]);

  const { data: subLimitSettings, isLoading: isLoadingSubLimitSettings } =
    useQuery(
      [EQueryKeys.SUB_LIMIT_SETTINGS, submission.submissionId],
      () =>
        subLimitSettingsApi.filterSubLimitSettings({
          filterSubLimitSettingsQueryDto: {
            submissionId: submission.submissionId,
          },
        }),
      {
        enabled: !!submission.submissionId,
      }
    );

  const rowData = useMemo(
    () =>
      subLimitSettings?.length
        ? subLimitSettings.map((item) => ({
            subLimitSettingId: item.subLimitSettingId,
            fire: item.fire,
            wind: item.wind,
            earthquake: item.earthquake,
            flood: item.flood,
            layerNumber: item.layerNumber,
          }))
        : [],
    [subLimitSettings]
  );

  const updateSubLimitSettingsMutation = useMutation(
    async (rowData: UpdateSubLimitSettingsPayloadDto[]) => {
      await subLimitSettingsApi.updateSubLimitSettings({
        updateSubLimitSettingsPayloadDto: rowData,
      });
    },
    {
      onError: async (error: { response: Response }) => {
        handleError({
          error,
          defaultMessage: t('perilSettings.recordUpdatedFailed'),
        });
      },
    }
  );

  const onCellValueChanged = useCallback(
    async (params: CellValueChangedEvent) => {
      const changedRow: UpdateSubLimitSettingsPayloadDto = {
        subLimitSettingId: params.data.subLimitSettingId,
        fire: params.data.fire,
        wind: params.data.wind,
        earthquake: params.data.earthquake,
        flood: params.data.flood,
      };
      await updateSubLimitSettingsMutation.mutateAsync([changedRow]);
    },
    [updateSubLimitSettingsMutation]
  );

  const perilColumnDefs = useMemo<ExcelTableColDef[]>(
    () => [
      {
        field: 'fire',
        headerName: t('perilSettings.headers.fire'),
        flex: 1,
        columnType: EExcelTableColumnType.CHECKBOX,
        checkboxLabel: t('perilSettings.includePeril'),
        editable: isEditingMode,
      },
      {
        field: 'wind',
        headerName: t('perilSettings.headers.wind'),
        flex: 1,
        columnType: EExcelTableColumnType.CHECKBOX,
        checkboxLabel: t('perilSettings.includePeril'),
        editable: isEditingMode,
      },
      {
        field: 'earthquake',
        headerName: t('perilSettings.headers.earthquake'),
        flex: 1,
        columnType: EExcelTableColumnType.CHECKBOX,
        checkboxLabel: t('perilSettings.includePeril'),
        editable: isEditingMode,
      },
      {
        field: 'flood',
        headerName: t('perilSettings.headers.flood'),
        flex: 1,
        columnType: EExcelTableColumnType.CHECKBOX,
        checkboxLabel: t('perilSettings.includePeril'),
        editable: isEditingMode,
      },
    ],
    [t, isEditingMode]
  );

  return (
    <div className="mb-4">
      <ExcelTable
        onCellValueChanged={isEditingMode ? onCellValueChanged : undefined}
        tableContainerClassName="h-[15rem]"
        loading={isLoadingSubLimitSettings}
        columnDefs={perilColumnDefs}
        data={rowData}
        tableClassName="rounded-t-lg"
        paginationType={EExcelTablePaginationType.INFO}
        includeIdRow={true}
      />
    </div>
  );
};

export default PerilSettingsTable;
